import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

// Space between the kebab and its panel - keep in step with the 2px
// margins on .toc-row-menu-panel / -up in _components.scss
const PANEL_GAP = 2

// Kebab (three-dots) menu shown on ToC rows when author tools are enabled.
// Renders a list of actions: [{key, label, icon, onSelect}]
// The panel is absolutely positioned within the row so it scrolls with the
// ToC; the .toc-row-menu-open class lifts it above later rows and relaxes
// the ancestor submenu overflow clipping (see _components.scss).
export const TocRowMenu = ({ title, actions }) => {
  const [open, setOpen] = useState(false)
  const [dropUp, setDropUp] = useState(false)
  const containerRef = useRef(null)
  const buttonRef = useRef(null)
  const panelRef = useRef(null)

  // #toc-content clips the panel (overflow-y: auto), so open it on whichever
  // side of the kebab fits inside that box. Computed from the anchor rect +
  // panel height (not the panel's own rect) so the answer doesn't depend on
  // which direction the panel happened to render in.
  useLayoutEffect(() => {
    if (!open) {
      setDropUp(false)
      return
    }
    const panel = panelRef.current
    const anchor = containerRef.current
    if (!panel || !anchor) return
    const scroller = anchor.closest('#toc-content')
    const bounds = scroller
      ? scroller.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight }
    setDropUp(
      shouldDropUp(anchor.getBoundingClientRect(), panel.offsetHeight, bounds)
    )
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const handleClickOutside = event => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // keep arrows/enter/escape from triggering the ToC-wide menu navigation
  const handleKeyDown = event => {
    event.stopPropagation()
    if (event.key === 'Escape') {
      setOpen(false)
      if (buttonRef.current) {
        buttonRef.current.focus()
      }
    }
  }

  return (
    <span
      className={`toc-row-menu${open ? ' toc-row-menu-open' : ''}`}
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        ref={buttonRef}
        className="toc-row-menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Options for ${title}`}
        onClick={event => {
          event.preventDefault()
          event.stopPropagation()
          setOpen(!open)
        }}
      >
        <i className="fa-solid fa-ellipsis-vertical" aria-hidden="true" />
      </button>
      {open && (
        <ul
          className={`dropdown-menu toc-row-menu-panel${
            dropUp ? ' toc-row-menu-panel-up' : ''
          }`}
          ref={panelRef}
          role="menu"
        >
          {actions.map(action => {
            if (action.divider) {
              return <li key={action.key} role="separator" className="divider" />
            }
            return (
              <li key={action.key} role="none" className="me-list-item">
                <button
                  type="button"
                  role="menuitem"
                  className={`toc-row-menu-action${
                    action.danger ? ' toc-row-menu-action-danger' : ''
                  }`}
                  onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    setOpen(false)
                    action.onSelect()
                  }}
                >
                  <i className={`${action.icon} fa-fw`} aria-hidden="true" />{' '}
                  {action.label}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </span>
  )
}

// Open downward unless the panel would run past the bottom of `bounds`.
// Flip up only when it fits above; when neither side fits, take the side
// with more room (a tie opens down).
export function shouldDropUp(anchorRect, panelHeight, bounds) {
  const roomBelow = bounds.bottom - anchorRect.bottom - PANEL_GAP
  const roomAbove = anchorRect.top - bounds.top - PANEL_GAP
  if (panelHeight <= roomBelow) return false
  if (panelHeight <= roomAbove) return true
  return roomAbove > roomBelow
}

// Builds the contextual action list for a ToC row.
// Third-level items only render in the ToC when nested under a level-2
// <chapter>, so "add inside" is only offered where the result will show up.
// Labels follow the tag the server will actually create (see add_toc_item):
// a sibling matches this row's tag, and a child matches the row's existing
// children - WGU-style books nest chapters where classic books hold sections.
export function tocMenuActions({ tag, childTag, depth, itemId, title, onAddItem, onStartRename, onStartDelete, }) {
  const actions = []
  const noun = t => (t === 'chapter' ? 'chapter' : 'section')
  if (depth === 1) {
    actions.push({
      key: 'add-after',
      label: 'Add chapter below',
      icon: 'fa-light fa-plus',
      onSelect: () => onAddItem(itemId, 'after'),
    })
    actions.push({
      key: 'add-inside',
      label: `Add ${noun(childTag)} inside`,
      icon: 'fa-light fa-indent',
      onSelect: () => onAddItem(itemId, 'inside'),
    })
  } else {
    actions.push({
      key: 'add-after',
      label: `Add ${noun(tag)} below`,
      icon: 'fa-light fa-plus',
      onSelect: () => onAddItem(itemId, 'after'),
    })
    if (depth === 2 && tag === 'chapter') {
      actions.push({
        key: 'add-inside',
        label: 'Add section inside',
        icon: 'fa-light fa-indent',
        onSelect: () => onAddItem(itemId, 'inside'),
      })
    }
  }
  actions.push({
    key: 'rename',
    label: 'Rename',
    icon: 'fa-light fa-pen',
    onSelect: () => onStartRename(itemId),
  })
  actions.push({ key: 'divider', divider: true })
  actions.push({
    key: 'delete',
    label: 'Delete',
    icon: 'fa-light fa-trash',
    danger: true,
    onSelect: () => onStartDelete(itemId, title),
  })
  return actions
}
